/** OnboardAgencyType — the multi-select step that sits BEFORE the
 *  main agency onboarding form.
 *
 *  Per user's spec (Feb 2026):
 *    · One company can select multiple types (Creative + Digital, etc.)
 *    · Office-floor desks unlock based on the union of selected types
 *    · Six seed types: Creative, OOH, Digital, Media Buying, Influencer,
 *      Activation. Casting & PR live in the film section, not here.
 *
 *  Chosen types flow into the next step via route state → the existing
 *  `/onboard/advertising-agency` form reads `location.state.agencyTypes`
 *  and persists it to the org record on submit.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import OnboardBackButton from "@/components/OnboardBackButton";

export default function OnboardAgencyType() {
  const nav = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get("/meta/agency-types")
       .then(({ data }) => setCatalog(data))
       .catch(() => setCatalog({ types: [] }));
  }, []);

  const toggle = (key) => {
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  };

  const proceed = () => {
    if (selected.length === 0) return;
    nav("/onboard/advertising-agency", { state: { agencyTypes: selected } });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14"
         data-testid="onboard-agency-type-page">
      <OnboardBackButton fallback="/onboard" testId="onboard-agency-type-back" />

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          L2 · Sign up · Step 1 of 2
        </div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
          What kind of agency are you?
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
          Pick every type that describes your business — one shop can be
          multiple (a Creative + Digital + Influencer agency is common).
          Your office floor and staffing roster will adapt to what you pick.
        </p>
      </div>

      {catalog === null ? (
        <div className="mt-10 text-white/50 text-sm">Loading…</div>
      ) : (
        <>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
               data-testid="agency-type-grid">
            {catalog.types.map((t) => {
              const Icon = Icons[t.icon] || Icons.Circle;
              const isSelected = selected.includes(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => toggle(t.key)}
                  className={
                    "text-left rounded-2xl border p-6 transition-all relative " +
                    (isSelected
                      ? "border-white bg-white/[0.06] shadow-[0_0_0_1px_rgba(255,255,255,0.4)]"
                      : "border-white/12 bg-black/25 hover:border-white/30 hover:bg-white/[0.03]")
                  }
                  data-testid={`agency-type-card-${t.key}`}
                >
                  {isSelected && (
                    <div className="absolute top-4 right-4 w-5 h-5 rounded-full bg-white text-black flex items-center justify-center"
                         data-testid={`agency-type-check-${t.key}`}>
                      <Check size={12} strokeWidth={3}/>
                    </div>
                  )}
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                       style={{ background: `${t.accent}22`, color: t.accent }}>
                    <Icon size={20} />
                  </div>
                  <div className="font-display text-lg uppercase tracking-tight">
                    {t.label}
                  </div>
                  <p className="mt-2 text-xs text-white/60 leading-relaxed">
                    {t.blurb}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <div className="text-xs text-white/55">
              {selected.length === 0
                ? "Pick at least one to continue."
                : `${selected.length} selected · ${selected.map((k) =>
                    catalog.types.find((t) => t.key === k)?.label
                  ).join(" · ")}`}
            </div>
            <button
              onClick={proceed}
              disabled={selected.length === 0}
              className="ml-auto btn-themed h-11 px-6 rounded-full uppercase tracking-[0.22em] text-[11px] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="agency-type-continue"
            >
              Continue <ArrowRight size={13}/>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
